import { FormLabelRequiredText } from './FormLabelRequiredText';

import styles from './FormLabel.module.scss';

type Props = {
  /**
   * How to vertically align the items.
   */
  alignItems?: 'center' | 'start';
  /**
   * The content to render within `FormLabel`.
   * May be text or nodes containing text, an `input`, etc.
   */
  children: React.ReactNode;
  /**
   * The `for` attribute to attach to the `label` node, associating it to
   * an external input by its `id`.
   */
  htmlFor?: string;
  /**
   * Indicates whether the input the `FormLabel` controls is disabled.
   */
  isDisabled?: boolean;
  /**
   * Indicates whether the input the `FormLabel` controls is errored.
   */
  isErrored?: boolean;
  /**
   * Indicates whether the input the `FormLabel` controls is required.
   */
  isRequired?: boolean;
  /**
   * Indicates whether the input the `FormLabel` controls is selected.
   */
  isSelected?: boolean;
  /**
   * How to horizontally justify the content.
   */
  justifyContent?: 'start' | 'stretch';
  /**
   * Size of the `FormLabel`.
   */
  size?: 'compact' | 'small' | 'medium';
};

/**
 * A styled form label. Wrap form components with it to attach `label` text, especially for accessibility.
 */
export const FormLabel = ({
  alignItems = 'center',
  children,
  htmlFor = undefined,
  isDisabled = undefined,
  isErrored = undefined,
  isRequired = undefined,
  isSelected = undefined,
  justifyContent = 'start',
  size = 'medium',
}: Props) => {
  return (
    <label
      className={styles.root}
      data-align-items={alignItems}
      data-justify-content={justifyContent}
      data-size={size}
      data-state={
        [
          isSelected && 'selected',
          isErrored && 'errored',
          isDisabled && 'disabled',
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
      data-zds
      htmlFor={htmlFor}
    >
      {children}

      {isRequired ? <FormLabelRequiredText isDisabled={isDisabled} /> : null}
    </label>
  );
};
